import React, { useState } from 'react';
import { useTable } from 'react-table';
import { ProductName, TableData, SmallHeader, Table, Link, Icon, Row, TableWrapper, Date } from './styles';
import SVGIcon from '../../assets/images/svg/SVGIcon';
import TableSubRow from '../tableSubRow';
import { isEmpty } from '../../helpers/common';
import { ActivityDataInterface, DataItem } from '../../interfaces/TimeReportInterface';
import Tooltip from '../tooltip';
import EmptyStateV2 from '../emptyState';
import { EMPTY_STATE_SIZE, ICON_SIZE } from '../../global/constants';
import { observer } from 'mobx-react-lite';
import Image from '../image';

interface mainColumns {
  Header: string;
  accessor: string;
}

interface TableComponentProps {
  columns: mainColumns[];
  data: DataItem[];
}
interface Props {
  width?: boolean;
  isFontWeight?: boolean;
  showIcon?: boolean;
  highlightLastRow?: boolean;
  isBorder?: boolean;
  isBorderBottom?: boolean;
  dataBorder?: boolean;
  handleOpen?: (item: ActivityDataInterface | DataItem) => void;
  isScrollTable?: boolean;
  isLastChildSticky?: boolean;
}
type CombinedProps = TableComponentProps & Props;
const MainTable: React.FC<CombinedProps> = ({
  columns,
  data,
  width,
  showIcon,
  highlightLastRow,
  isBorder,
  isBorderBottom,
  dataBorder,
  handleOpen,
  isScrollTable,
  isLastChildSticky
}) => {
  //States
  const [isTableOpen, setIsTableOpen] = useState(false);
  const [selectedRow, setSelectedRow] = useState(-1);
  //Other variable
  const { getTableProps, getTableBodyProps, headerGroups, rows, prepareRow } = useTable({ columns, data });

  const handleLinkButtonClick = (rowIndex: number) => {
    if (selectedRow === rowIndex) {
      // Close the current row if clicking the same row
      setIsTableOpen(false);
      setSelectedRow(-1);
    } else {
      // Open the new row and close the previous one
      setIsTableOpen(true);
      setSelectedRow(rowIndex);
    }
  };

  return (
    /* eslint-disable react/jsx-props-no-spreading */

    <TableWrapper isScrollTable={isScrollTable}>
      <Table {...getTableProps()} isScrollTable={isScrollTable}>
        <thead>
          {rows?.length > 0 &&
            headerGroups.map((headerGroup) => (
              <tr {...headerGroup.getHeaderGroupProps()} key={headerGroup?.id}>
                {headerGroup.headers.map((column, columnIndex) => (
                  <SmallHeader
                    {...column.getHeaderProps()}
                    key={column.id}
                    isBorderBottom={isBorderBottom}
                    isScrollTable={isScrollTable}
                    isLastChildSticky={isLastChildSticky}
                    className={columnIndex === headerGroup.headers.length - 2 ? 'no-border-right' : ''}>
                    {column.render('Header')}
                  </SmallHeader>
                ))}
              </tr>
            ))}
        </thead>
        <tbody {...getTableBodyProps()}>
          {rows?.length > 0 &&
            rows.map((row, index) => {
              prepareRow(row);
              return (
                <React.Fragment key={row?.id}>
                  <tr {...row.getRowProps()}>
                    {row.cells.map((cell, cellIndex) => (
                      <TableData
                        {...cell.getCellProps()}
                        key={cell.column.id}
                        isWidth={width ? true : false}
                        isBorder={isBorder}
                        isScrollTable={isScrollTable}
                        isLastChildSticky={isLastChildSticky}
                        isBorderTop={dataBorder && index === 0}
                        isFontWeight={highlightLastRow && index === rows.length - 1}>
                        {cell.column.id === 'screenshots' ? (
                          <div className='image-data'>
                            {!isEmpty(cell.row.original.screenshotImage) && (
                              <Image
                                src={cell.row.original.screenshotImage}
                                alt='Screenshot'
                                width={36}
                                height={36}
                                className='screen-shot'
                                onClick={() => handleOpen && handleOpen(cell.row.original)}
                                borderRadius='var(--border-radius-full);'
                              />
                            )}
                            <span>{cell.value}</span>
                          </div>
                        ) : cell.column.id === 'activityName' ? (
                          <Tooltip title={cell.value} maxWidth={'max-content'} placement='top' className='tooltip-text'>
                            <div className='truncate-text'>
                              <p>{cell.value}</p>
                            </div>
                          </Tooltip>
                        ) : cell.column.id === 'date' ? (
                          cell.row.index === 0 || data[cell.row.index].date !== data[cell.row.index - 1].date ? (
                            <Date>
                              <span className='month'>{cell.value.split(' ')[0]}</span>
                              <span className='day'>{cell.value.split(' ')[1]}</span>
                            </Date>
                          ) : null
                        ) : cellIndex === 0 ? (
                          <>
                            <Row>
                              {showIcon && (
                                <Link>
                                  <Icon role='button' onClick={() => handleLinkButtonClick(index)}>
                                    <SVGIcon
                                      name={selectedRow !== index ? 'plus-icon' : 'updated-minus-icon'}
                                      width={ICON_SIZE.MD}
                                      height={ICON_SIZE.MD}
                                      viewBox={`0 0 ${ICON_SIZE.LG} ${ICON_SIZE.LG}`}
                                      fill='var(--brand-primary)'
                                    />
                                  </Icon>
                                </Link>
                              )}
                              <ProductName isFontWeight={highlightLastRow && index === rows.length - 1}>
                                {String(cell.row.original[columns[0].accessor])}
                              </ProductName>
                            </Row>
                          </>
                        ) : (
                          cell.render('Cell')
                        )}
                      </TableData>
                    ))}
                  </tr>

                  {isTableOpen && selectedRow === index && (
                    <TableSubRow
                      columns={[
                        { Header: 'Person Name', accessor: 'name' },
                        { Header: 'Time', accessor: 'time' },
                        { Header: 'Amount', accessor: 'amount' }
                      ]}
                      data={data[index]?.items as DataItem[]}
                    />
                  )}
                </React.Fragment>
              );
            })}

          {rows?.length === 0 && (
            <tr>
              <td>
                <EmptyStateV2
                  size={EMPTY_STATE_SIZE.SMALL}
                  imageUrl={'reports_empty'}
                  title={`You don't have reports.`}
                  description='There are no report data for this date. Please try another Filters.'
                  className='reports-time-empty'
                />
              </td>
            </tr>
          )}
        </tbody>
      </Table>
    </TableWrapper>
  );
};

export default observer(MainTable);
